/**
 * File-type classification for attachment thumbnails.
 *
 * MIME type is the primary signal because it is what the browser reported at
 * upload time; the extension is the fallback for the many files that arrive as
 * `application/octet-stream` (anything dragged in from a network share, and
 * every legacy row stored before content types were kept). Neither is trusted
 * for anything but presentation — the badge on a tile is a hint, not a claim.
 */

import type { AttachmentDraft } from "../types";

/** Visual families. Each maps to one badge colour and one glyph. */
export type AttachmentFamily =
  | "image"
  | "pdf"
  | "doc"
  | "sheet"
  | "slide"
  | "video"
  | "audio"
  | "archive"
  | "text"
  | "file";

export interface AttachmentKind {
  family: AttachmentFamily;
  /** Human label for the popover ("PDF document", "Spreadsheet"). */
  label: string;
  /** Short badge text on the icon thumbnail — the extension where there is one. */
  badge: string;
}

const FAMILY_LABELS: Record<AttachmentFamily, string> = {
  image: "Image",
  pdf: "PDF document",
  doc: "Word document",
  sheet: "Spreadsheet",
  slide: "Presentation",
  video: "Video",
  audio: "Audio",
  archive: "Archive",
  text: "Text file",
  file: "File",
};

/** Extension → family, for files whose MIME type says nothing useful. */
const EXTENSION_FAMILIES: Record<string, AttachmentFamily> = {
  jpg: "image",
  jpeg: "image",
  png: "image",
  gif: "image",
  bmp: "image",
  webp: "image",
  tif: "image",
  tiff: "image",
  heic: "image",
  avif: "image",
  svg: "image",
  pdf: "pdf",
  doc: "doc",
  docx: "doc",
  odt: "doc",
  rtf: "doc",
  pages: "doc",
  xls: "sheet",
  xlsx: "sheet",
  xlsm: "sheet",
  csv: "sheet",
  ods: "sheet",
  numbers: "sheet",
  ppt: "slide",
  pptx: "slide",
  odp: "slide",
  key: "slide",
  mp4: "video",
  m4v: "video",
  mov: "video",
  avi: "video",
  mkv: "video",
  webm: "video",
  mp3: "audio",
  m4a: "audio",
  wav: "audio",
  aac: "audio",
  ogg: "audio",
  zip: "archive",
  rar: "archive",
  "7z": "archive",
  tar: "archive",
  gz: "archive",
  txt: "text",
  md: "text",
  log: "text",
  json: "text",
  xml: "text",
};

/** MIME subtype fragments that identify office formats across their many names. */
const MIME_FAMILIES: ReadonlyArray<[RegExp, AttachmentFamily]> = [
  [/^image\//, "image"],
  [/^video\//, "video"],
  [/^audio\//, "audio"],
  [/pdf/, "pdf"],
  [/word|opendocument\.text|rtf/, "doc"],
  [/sheet|excel|csv|opendocument\.spreadsheet/, "sheet"],
  [/presentation|powerpoint/, "slide"],
  [/zip|compressed|tar|rar|7z/, "archive"],
  [/^text\//, "text"],
];

/** The trailing extension in lower case, or "" when the name carries none. */
export function fileExtension(name: string): string {
  const match = /\.([A-Za-z0-9]{1,8})$/.exec(name.trim());
  return match ? match[1].toLowerCase() : "";
}

/**
 * Classifies one attachment for display.
 *
 * @param {string} name - Display file name.
 * @param {string} type - Reported MIME type (may be empty or octet-stream).
 * @returns {AttachmentKind} Family, label, and badge text.
 */
export function attachmentKind(name: string, type: string): AttachmentKind {
  const extension = fileExtension(name);
  const mime = (type ?? "").toLowerCase();
  const byMime = MIME_FAMILIES.find(([pattern]) => pattern.test(mime))?.[1];
  const family = byMime ?? EXTENSION_FAMILIES[extension] ?? "file";
  return {
    family,
    label: FAMILY_LABELS[family],
    badge: extension ? extension.toUpperCase() : family === "file" ? "FILE" : FAMILY_LABELS[family].toUpperCase(),
  };
}

/**
 * True when the attachment can be shown as a picture: it is an image *and*
 * there is somewhere to load it from (a staged preview URL, or a persisted row
 * whose bytes the API will serve). An image with neither is still a file row.
 */
export function isViewableImage(attachment: AttachmentDraft, jobId?: string): boolean {
  if (attachmentKind(attachment.name, attachment.type).family !== "image") return false;
  return Boolean(attachment.previewUrl) || Boolean(attachment.persisted && jobId);
}

/**
 * True when the attachment can be played inline: it is audio *and* there is
 * somewhere to load the bytes from (a staged `blob:` URL, or a persisted row the
 * API serves). Audio with neither is still a plain file row.
 */
export function isPlayableAudio(attachment: AttachmentDraft, jobId?: string): boolean {
  if (attachmentKind(attachment.name, attachment.type).family !== "audio") return false;
  return Boolean(attachment.previewUrl) || Boolean(attachment.persisted && jobId);
}

/** Source URL for an attachment's bytes, or null when there is nothing to load. */
export function attachmentSrc(attachment: AttachmentDraft, jobId?: string): string | null {
  if (attachment.previewUrl) return attachment.previewUrl;
  if (attachment.persisted && jobId) return `/api/jobs/${jobId}/attachments/${attachment.id}`;
  return null;
}

/** "3 Sep 2026, 2:15 pm" — absolute, because "2 days ago" is useless in a file list. */
export function formatAttachmentDate(iso?: string): string {
  if (!iso) return "Unknown";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
